import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  Shield, 
  Coins, 
  Download, 
  Upload, 
  Clock, 
  Lock, 
  CheckCircle, 
  AlertTriangle, 
  FileText, 
  Users, 
  CreditCard, 
  TrendingUp, 
  UserCheck, 
  Filter, 
  Plus, 
  ListTodo, 
  Eye, 
  EyeOff, 
  User, 
  Search,
  Check,
  RefreshCw,
  FolderLock,
  ChevronRight,
  Database,
  Briefcase,
  HelpCircle,
  FileCheck,
  AlertCircle
} from 'lucide-react';

// Safe warning copy elements mandated (HMRC / non-auto filing disclaimer)
const SAFETY_DISCLAIMER = "Tax readiness tools built in-house. Professional review required before tax filing, Self Assessment submission, payroll submission, VAT filing, or final company accounts. This system prepares records for Self Assessment review and does not submit a tax return to HMRC automatically.";

interface AuditLog {
  userId: string;
  recordType: string;
  recordId: string;
  accessTime: string;
  action: string;
  ipHash: string;
  userAgentHash: string;
  approvalStatus: string;
}

export function NexusTaxReadinessHub() {
  // Mode selection (Super Admin, Owner / Director, Admin, Staff)
  const [activeRole, setActiveRole] = useState<'SUPER_ADMIN' | 'DIRECTOR' | 'ADMIN' | 'STAFF'>('SUPER_ADMIN');
  const [activeSubTab, setActiveSubTab] = useState<string>('DIRECTOR_HUB');
  
  // Data States
  const [directorSummary, setDirectorSummary] = useState<any>({
    taxYear: "2025/2026",
    directorSalary: 12570,
    dividendsReceived: 45000,
    benefitsInKind: 2400,
    directorLoans: 0,
    expensesReimbursed: 1850.50,
    mileageClaims: 480,
    otherEmploymentIncome: 0,
    selfEmploymentIncome: 0,
    bankInterest: 154.20,
    rentalIncome: 0,
    capitalGains: 0,
    pensionContributions: 3600,
    charitableGiving: 500,
    studentLoan: 0,
    paymentsOnAccount: 4500,
    accountantReviewStatus: "Review Pending"
  });

  const [checklist, setChecklist] = useState<any[]>([]);
  const [staffRecords, setStaffRecords] = useState<any[]>([]);
  const [taxDocuments, setTaxDocuments] = useState<any[]>([]);
  const [p60P45P11D, setP60P45P11D] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [deadlines, setDeadlines] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  
  // Loading & Action states
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Modals / In-line builders
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showAddContractor, setShowAddContractor] = useState(false);

  // New item draft states
  const [newExpense, setNewExpense] = useState({
    employeeName: "Lead Director",
    expenseType: "Travel",
    benefitType: "Other benefit",
    amount: "",
    purpose: "",
    reimbursed: "Yes"
  });

  const [newDoc, setNewDoc] = useState({
    name: "",
    type: "Payslip",
    ownerId: "EMP-001"
  });

  const [newContractor, setNewContractor] = useState({
    contractorName: "",
    businessName: "",
    invoiceNo: "",
    serviceProvided: "",
    amount: "",
    taxStatus: "Gross Payment"
  });

  // Simulated export pack
  const [exportData, setExportData] = useState<any>(null);

  // Fetch all initial data
  const loadData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Director summary & Checklist
      const summaryRes = await fetch('/api/tax/self-assessment/summary');
      if (summaryRes.ok) {
        const payload = await summaryRes.json();
        setDirectorSummary(payload.data);
        setChecklist(payload.checklist);
      }

      // 2. Fetch staff payroll records
      const staffRes = await fetch(`/api/tax/staff-records?userRole=${activeRole}&userEmail=siddique.mahmood@gmail.com`);
      if (staffRes.ok) {
        const payload = await staffRes.json();
        setStaffRecords(payload.data || []);
      }

      // 3. Fetch tax documents & logs
      const docRes = await fetch(`/api/tax/documents?userRole=${activeRole}&userEmail=siddique.mahmood@gmail.com`);
      if (docRes.ok) {
        const payload = await docRes.json();
        setTaxDocuments(payload.data || []);
        setAuditLogs(payload.logs || []);
      }

      // 4. Fetch P60/P45/P11D tracking
      const trackerRes = await fetch('/api/tax/p60-p45-p11d');
      if (trackerRes.ok) {
        const payload = await trackerRes.json();
        setP60P45P11D(payload.data || []);
      }

      // 5. Fetch Expenses
      const expenseRes = await fetch('/api/tax/expenses-benefits');
      if (expenseRes.ok) {
        const payload = await expenseRes.json();
        setExpenses(payload.data || []);
      }

      // 6. Fetch Deadlines
      const deadlineRes = await fetch('/api/tax/deadlines');
      if (deadlineRes.ok) {
        const payload = await deadlineRes.json();
        setDeadlines(payload.data || []);
      }

      // Mock contractors - fallback or load
      setContractors([
        { id: "CON-001", contractorName: "Alex Mercer", businessName: "Apex Cybersec Ltd", invoiceNo: "INV-2026-90", serviceProvided: "Subnet Penetration Testing", amount: 4200, paymentStatus: "Paid", taxStatus: "Gross Payment (No Retainage)", contractStatus: "Active", ir35ReviewPlaceholder: "Outside IR35 (NexoraX B2B SLA)", accountantReviewStatus: "Completed" },
        { id: "CON-002", contractorName: "Zainab Malik", businessName: "Malik Softworks", invoiceNo: "INV-2026-12", serviceProvided: "Cognitive Layer Training", amount: 3100, paymentStatus: "Pending", taxStatus: "Gross Payment", contractStatus: "Under Review", ir35ReviewPlaceholder: "Outside IR35 (Fully independent schedule)", accountantReviewStatus: "Review Pending" }
      ]);
    } catch (e) {
      console.error("Failed to sync tax data", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [activeRole]);

  // Handle Updates
  const handleUpdateChecklist = async (id: string) => {
    const updatedChecklist = checklist.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    );
    setChecklist(updatedChecklist);
    try {
      await fetch('/api/tax/self-assessment/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checklist: updatedChecklist })
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateDirectorIncome = async (field: string, val: number) => {
    const updated = { ...directorSummary, [field]: val };
    setDirectorSummary(updated);
    try {
      await fetch('/api/tax/director-income', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated)
      });
      showNotification("Director parameter synchronized", "success");
    } catch (err) {
      console.error(err);
      showNotification("Failed to synchronize with imperial ledger node", "error");
    }
  };

  const handleAddExpenseSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newExpense.amount || isNaN(Number(newExpense.amount))) return;

    const payload = {
      employeeName: newExpense.employeeName,
      date: new Date().toISOString().split('T')[0],
      expenseType: newExpense.expenseType,
      benefitType: newExpense.benefitType,
      amount: Number(newExpense.amount),
      reimbursed: newExpense.reimbursed,
      purpose: newExpense.purpose || "Business operational needs",
      receiptAttached: "Yes",
      taxableStatus: newExpense.expenseType === "Mileage" ? "No (Approved Mileage Rate)" : "No (Business Expense)",
      payrollTreatment: "Exempt",
      p11DTreatment: "Not Reportable",
      accountantReviewStatus: "Review Pending"
    };

    try {
      const res = await fetch('/api/tax/expenses-benefits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ record: payload })
      });
      if (res.ok) {
        const data = await res.json();
        setExpenses(data.data);
        setShowAddExpense(false);
        setNewExpense({
          employeeName: "Lead Director",
          expenseType: "Travel",
          benefitType: "Other benefit",
          amount: "",
          purpose: "",
          reimbursed: "Yes"
        });
        showNotification("Expense claim recorded in register", "success");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddDocSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDoc.name) return;

    try {
      const res = await fetch('/api/tax/document-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newDoc.name,
          type: newDoc.type,
          ownerId: newDoc.ownerId,
          size: `${Math.floor(Math.random() * 200 + 100)} KB`
        })
      });
      if (res.ok) {
        const data = await res.json();
        setTaxDocuments(data.data);
        setShowAddDoc(false);
        setNewDoc({ name: "", type: "Payslip", ownerId: "EMP-001" });
        showNotification("Document secured and audit log locked", "success");
        loadData(); // reload audit logs
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleAuditReviewStatus = async (type: string, id?: string, status?: string) => {
    const finalStatus = status || "Completed";
    try {
      const res = await fetch('/api/tax/review-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, id, status: finalStatus })
      });
      if (res.ok) {
        showNotification(`Tax block successfully marked: ${finalStatus}`, "success");
        loadData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const triggerAccountantExport = async (type: string) => {
    try {
      const res = await fetch(`/api/tax/accountant-export?type=${type}&userEmail=siddique.mahmood@gmail.com`);
      if (res.ok) {
        const data = await res.json();
        setExportData(data);
        showNotification(`Secured accountant audit pack generated matching year 2025/2026!`, "success");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const showNotification = (text: string, type: 'success' | 'error') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  };

  // Helper values
  const totalDividends = directorSummary.dividendsReceived;
  const totalSalary = directorSummary.directorSalary;
  const totalBenefits = directorSummary.benefitsInKind;
  const totalReimbursed = directorSummary.expensesReimbursed;
  const estimatedTaxableIncome = totalSalary + totalDividends + totalBenefits;

  return (
    <div id="nexus-tax-readiness-hub" className="w-full text-slate-100 font-sans space-y-6">
      
      {/* Alert banner for live messages */}
      <AnimatePresence>
        {message && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-xl border font-mono text-xs flex items-center gap-3 shadow-2xl backdrop-blur-md ${
              message.type === 'success' 
                ? 'bg-emerald-950/90 border-emerald-500/40 text-emerald-300' 
                : 'bg-rose-950/90 border-rose-500/40 text-rose-300'
            }`}
          >
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <span>{message.text}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Primary Header Segment */}
      <div className="bg-[#02050e]/90 border border-purple-500/20 rounded-2xl p-6 relative overflow-hidden shadow-[0_0_20px_rgba(168,85,247,0.1)]">
        <div className="absolute top-0 right-0 w-80 h-40 bg-gradient-to-l from-purple-500/10 to-transparent blur-3xl rounded-full"></div>
        <div className="absolute top-0 left-0 w-2 h-full bg-gradient-to-b from-cyan-400 to-purple-600"></div>

        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 z-10 relative">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 rounded-full text-[8.5px] font-mono font-bold tracking-widest uppercase bg-purple-900/40 text-purple-300 border border-purple-500/30">
                PROVISIONAL COMPLIANCE GUARD
              </span>
              <span className="text-amber-400 text-xs font-mono font-black animate-pulse">
                [ 0.8s standard active ]
              </span>
            </div>
            <h2 className="text-2xl font-black font-sans text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-200 to-purple-400 uppercase tracking-wide">
              Nexus Tax & Self Assessment Readiness Hub
            </h2>
            <p className="text-xs text-slate-400 max-w-2xl font-mono">
              Director Self Assessment. Staff Tax Records. Payroll Evidence. Accountant Export.
            </p>
          </div>

          {/* Interactive Role Switcher for local previewing */}
          <div className="bg-black/60 border border-white/5 rounded-xl p-1.5 flex flex-wrap gap-1">
            <span className="text-[9px] font-mono text-slate-500 uppercase flex items-center px-2 mr-1">Preview Role:</span>
            {(['SUPER_ADMIN', 'DIRECTOR', 'ADMIN', 'STAFF'] as const).map((role) => (
              <button
                key={role}
                onClick={() => {
                  setActiveRole(role);
                  // Default tab based on role selected
                  if (role === 'STAFF') setActiveSubTab('STAFF_COMP');
                  else if (role === 'ADMIN') setActiveSubTab('PAYROLL_RECORDS');
                  else setActiveSubTab('DIRECTOR_HUB');
                }}
                className={`px-2.5 py-1 text-[9px] font-mono font-bold uppercase rounded-lg transition-all cursor-pointer ${
                  activeRole === role
                    ? 'bg-purple-600/30 border border-purple-500/50 text-purple-200'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {role.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Warning notification about HMRC submissions */}
        <div className="mt-5 p-3 rounded-xl bg-purple-950/15 border border-purple-500/20 text-[10px] text-purple-300/90 font-mono flex items-start gap-2.5">
          <AlertCircle className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
          <p className="leading-relaxed font-sans">{SAFETY_DISCLAIMER}</p>
        </div>
      </div>

      {/* Main navigation layouts */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Hub Side selector */}
        <div className="lg:col-span-3 space-y-3">
          <div className="bg-[#02050e]/80 border border-white/5 p-4 rounded-xl space-y-1">
            <span className="text-[10px] text-slate-500 font-mono uppercase tracking-widest block mb-2 font-bold px-1">
              Active Core Modules
            </span>

            {/* Hub tabs filtered by viewing permissions */}
            {['SUPER_ADMIN', 'DIRECTOR'].includes(activeRole) && (
              <>
                <button
                  onClick={() => setActiveSubTab('DIRECTOR_HUB')}
                  className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                    activeSubTab === 'DIRECTOR_HUB' 
                      ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  <span>Director Assessment Hub</span>
                  <Coins className="w-3.5 h-3.5 text-purple-400" />
                </button>
                <button
                  onClick={() => setActiveSubTab('DIVIDENDS_REGISTER')}
                  className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                    activeSubTab === 'DIVIDENDS_REGISTER' 
                      ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  <span>Director Income & Dividends</span>
                  <TrendingUp className="w-3.5 h-3.5 text-purple-400" />
                </button>
              </>
            )}

            {['SUPER_ADMIN', 'ADMIN'].includes(activeRole) && (
              <>
                <button
                  onClick={() => setActiveSubTab('PAYROLL_RECORDS')}
                  className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                    activeSubTab === 'PAYROLL_RECORDS' 
                      ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  <span>Payroll Records Vault</span>
                  <Database className="w-3.5 h-3.5 text-purple-400" />
                </button>
                <button
                  onClick={() => setActiveSubTab('TAX_TRACKER')}
                  className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                    activeSubTab === 'TAX_TRACKER' 
                      ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  <span>P60 / P45 / P11D Tracker</span>
                  <FileCheck className="w-3.5 h-3.5 text-purple-400" />
                </button>
                <button
                  onClick={() => setActiveSubTab('STAFF_INDEX')}
                  className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                    activeSubTab === 'STAFF_INDEX' 
                      ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  <span>Staff Tax Readiness Index</span>
                  <Users className="w-3.5 h-3.5 text-purple-400" />
                </button>
              </>
            )}

            {/* General Tabs */}
            {['SUPER_ADMIN', 'ADMIN', 'DIRECTOR'].includes(activeRole) && (
              <>
                <button
                  onClick={() => setActiveSubTab('EXPENSES_BENEFITS')}
                  className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                    activeSubTab === 'EXPENSES_BENEFITS' 
                      ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  <span>Expenses & Benefits</span>
                  <CreditCard className="w-3.5 h-3.5 text-purple-400" />
                </button>
                <button
                  onClick={() => setActiveSubTab('CONTRACTORS')}
                  className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                    activeSubTab === 'CONTRACTORS' 
                      ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                      : 'text-slate-400 hover:bg-white/5'
                  }`}
                >
                  <span>Contractors & Freelancers</span>
                  <Briefcase className="w-3.5 h-3.5 text-purple-400" />
                </button>
              </>
            )}

            {/* Documents & Deadlines */}
            {['SUPER_ADMIN', 'ADMIN', 'DIRECTOR'].includes(activeRole) && (
              <button
                onClick={() => setActiveSubTab('DEADLINES_TRACK')}
                className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                  activeSubTab === 'DEADLINES_TRACK' 
                    ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                    : 'text-slate-400 hover:bg-white/5'
                }`}
              >
                <span>Deadlines & Reminders</span>
                <Clock className="w-3.5 h-3.5 text-purple-400" />
              </button>
            )}

            {/* Document Vault Tab */}
            <button
              onClick={() => setActiveSubTab('DOCUMENT_VAULT')}
              className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                activeSubTab === 'DOCUMENT_VAULT' 
                  ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                  : 'text-slate-400 hover:bg-white/5'
              }`}
            >
              <span>Tax Document Vault</span>
              <Lock className="w-3.5 h-3.5 text-purple-400" />
            </button>

            {/* Accountant Export (Restricted to Admins & Directors) */}
            {['SUPER_ADMIN', 'ADMIN', 'DIRECTOR'].includes(activeRole) && (
              <button
                onClick={() => setActiveSubTab('EXPORTS_CENTRE')}
                className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                  activeSubTab === 'EXPORTS_CENTRE' 
                    ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                    : 'text-slate-400 hover:bg-white/5'
                }`}
              >
                <span>Accountant Export Pack</span>
                <Download className="w-3.5 h-3.5 text-amber-500" />
              </button>
            )}

            {/* Staff portal component */}
            {activeRole === 'STAFF' && (
              <button
                onClick={() => setActiveSubTab('STAFF_COMP')}
                className={`w-full text-left py-2.5 px-3 rounded-lg text-xs font-mono font-bold tracking-wider uppercase transition-all flex items-center justify-between ${
                  activeSubTab === 'STAFF_COMP' 
                    ? 'bg-purple-900/30 text-purple-300 border-l-2 border-purple-500 pl-4' 
                    : 'text-slate-400 hover:bg-white/5'
                }`}
              >
                <span>My Tax & Payroll Status</span>
                <UserCheck className="w-3.5 h-3.5 text-purple-450" />
              </button>
            )}
          </div>

          {/* Quick Metrics display */}
          <div className="bg-[#02050e]/60 border border-white/5 p-4 rounded-xl text-left space-y-3 font-mono text-[10px]">
            <span className="text-[9px] uppercase text-slate-500 block font-bold">Secure Access Metrics</span>
            <div className="flex justify-between">
              <span className="text-slate-500">Document Security:</span>
              <span className="text-cyan-400 font-bold">Private by Default</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">MFA Handshake:</span>
              <span className="text-emerald-400 font-bold">ACTIVE</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">HMRC Auto-Submit:</span>
              <span className="text-rose-400 font-bold">DISABLED</span>
            </div>
          </div>
        </div>

        {/* Content Panel Area */}
        <div className="lg:col-span-9 space-y-6 text-left">
          
          {loading && (
            <div className="p-8 text-center bg-black/40 border border-white/5 rounded-2xl flex items-center justify-center gap-3 font-mono text-xs">
              <RefreshCw className="w-4 h-4 animate-spin text-purple-500" />
              <span>Synchronizing secure registry entries...</span>
            </div>
          )}

          {!loading && (
            <AnimatePresence mode="wait">
              
              {/* TAB: Director Assessment Preparation Hub */}
              {activeSubTab === 'DIRECTOR_HUB' && (
                <motion.div
                  key="director-hub"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  {/* Summary & Estimators */}
                  <div className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                      <div>
                        <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                          <Coins className="text-purple-400" size={18} /> Owner & Director Self Assessment Hub ({directorSummary.taxYear})
                        </h4>
                        <span className="text-[10px] text-slate-400 font-mono block mt-1 uppercase">
                          Preparation matrix for director self assessment logs. Subject to executive accountant review.
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[9px] font-mono text-slate-400 uppercase">Review Status:</span>
                        <span className={`px-2.5 py-1 rounded text-[8px] font-mono font-bold uppercase tracking-wider ${
                          directorSummary.accountantReviewStatus === 'Completed' 
                            ? 'bg-emerald-950/40 text-emerald-400 border border-emerald-500/30'
                            : directorSummary.accountantReviewStatus === 'Under Review'
                            ? 'bg-blue-950/50 text-blue-300 border border-blue-500/30'
                            : 'bg-amber-950/40 text-amber-500 border border-amber-500/30'
                        }`}>
                          {directorSummary.accountantReviewStatus}
                        </span>
                        {directorSummary.accountantReviewStatus !== 'Completed' && (
                          <button
                            onClick={() => handleAuditReviewStatus('DIRECTOR_ASMT', undefined, 'Completed')}
                            className="px-2 py-0.5 bg-purple-900/60 hover:bg-purple-900 border border-purple-500/40 text-purple-200 text-[8.5px] rounded font-mono uppercase tracking-widest cursor-pointer font-bold"
                          >
                            Mark Reviewed
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Numerical estimators sliders & entries */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      
                      {/* Left: Input sliders for live recalculation summary */}
                      <div className="space-y-4">
                        <h5 className="text-xs font-mono uppercase font-bold text-cyan-400 tracking-wider">
                          Key Personal Income Metrics
                        </h5>
                        
                        <div className="space-y-3 font-mono text-[10px]">
                          <div>
                            <div className="flex justify-between text-slate-400 mb-1">
                              <span>Director Salary (PAYE):</span>
                              <span className="text-white font-extrabold font-mono">£{totalSalary.toLocaleString()}</span>
                            </div>
                            <input 
                              type="range" min="0" max="50000" step="500" 
                              value={directorSummary.directorSalary} 
                              onChange={(e) => handleUpdateDirectorIncome('directorSalary', Number(e.target.value))}
                              className="w-full accent-cyan-400 bg-zinc-950 h-1.5 rounded-lg appearance-none cursor-pointer"
                            />
                          </div>

                          <div>
                            <div className="flex justify-between text-slate-400 mb-1">
                              <span>Dividends Paid to Director:</span>
                              <span className="text-amber-400 font-extrabold font-mono">£{totalDividends.toLocaleString()}</span>
                            </div>
                            <input 
                              type="range" min="0" max="150000" step="1000" 
                              value={directorSummary.dividendsReceived} 
                              onChange={(e) => handleUpdateDirectorIncome('dividendsReceived', Number(e.target.value))}
                              className="w-full accent-amber-400 bg-zinc-950 h-1.5 rounded-lg appearance-none cursor-pointer"
                            />
                          </div>

                          <div>
                            <div className="flex justify-between text-slate-400 mb-1">
                              <span>Benefits in Kind (P11D):</span>
                              <span className="text-purple-400 font-extrabold font-mono">£{totalBenefits.toLocaleString()}</span>
                            </div>
                            <input 
                              type="range" min="0" max="15000" step="250" 
                              value={directorSummary.benefitsInKind} 
                              onChange={(e) => handleUpdateDirectorIncome('benefitsInKind', Number(e.target.value))}
                              className="w-full accent-purple-400 bg-zinc-950 h-1.5 rounded-lg appearance-none cursor-pointer"
                            />
                          </div>

                          <div>
                            <div className="flex justify-between text-slate-400 mb-1">
                              <span>Reimbursed Business Expenses:</span>
                              <span className="text-emerald-400 font-extrabold font-mono">£{totalReimbursed.toLocaleString()}</span>
                            </div>
                            <input 
                              type="range" min="0" max="10000" step="100" 
                              value={directorSummary.expensesReimbursed} 
                              onChange={(e) => handleUpdateDirectorIncome('expensesReimbursed', Number(e.target.value))}
                              className="w-full accent-emerald-400 bg-zinc-950 h-1.5 rounded-lg appearance-none cursor-pointer"
                            />
                          </div>
                        </div>

                        {/* Extra fields placeholders */}
                        <div className="grid grid-cols-2 gap-3 text-[10px] font-mono pt-2">
                          <div className="p-2.5 rounded-lg bg-black/40 border border-white/5 space-y-1">
                            <span className="text-slate-500 uppercase block text-[8px]">Director Loans Balance</span>
                            <span className="text-white font-bold block">£{directorSummary.directorLoans}</span>
                          </div>
                          <div className="p-2.5 rounded-lg bg-black/40 border border-white/5 space-y-1">
                            <span className="text-slate-500 uppercase block text-[8px]">Mileage Claims logged</span>
                            <span className="text-white font-bold block">£{directorSummary.mileageClaims}</span>
                          </div>
                        </div>
                      </div>

                      {/* Right: Personal Tax Checklist Screen 2 */}
                      <div className="bg-black/40 rounded-xl p-4 border border-white/5 space-y-3 flex flex-col justify-between">
                        <div>
                          <h5 className="text-xs font-mono uppercase font-black text-amber-400 tracking-wider flex items-center justify-between pb-1.5 border-b border-white/5">
                            <span>NexoraX Checklist Nodes</span>
                            <ListTodo className="w-4 h-4 text-amber-500" />
                          </h5>
                          <span className="text-[8.5px] font-mono uppercase text-slate-500 block mb-3">
                            Checklist of documents and declarations required for Self Assessment.
                          </span>

                          <div className="space-y-2 overflow-y-auto max-h-[160px] pr-1 scrollbar-thin">
                            {checklist.map((item) => (
                              <label key={item.id} className="flex items-start gap-2.5 cursor-pointer hover:bg-white/2 p-1 rounded transition-colors text-[9.5px]">
                                <input
                                  type="checkbox"
                                  checked={item.checked}
                                  onChange={() => handleUpdateChecklist(item.id)}
                                  className="mt-0.5 rounded bg-zinc-950 border-zinc-800 accent-purple-500 text-purple-600 focus:ring-0 focus:ring-offset-0"
                                />
                                <span className={`${item.checked ? 'text-slate-500 line-through' : 'text-slate-350'}`}>
                                  {item.text}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="pt-2 border-t border-white/5 text-[9px] font-mono text-slate-500 uppercase leading-relaxed text-left">
                          <span className="text-[#00e5ff] font-bold block mb-0.5">MANDATED DEADLINES SUMMARY:</span>
                          5 Oct (SA Registration) | 31 Oct (Paper submissions) | 31 Jan (Online filing & Balance payment) | 31 Jul (Payment on account split).
                        </div>
                      </div>

                    </div>

                    {/* Highlight summary card */}
                    <div className="bg-zinc-950/80 border border-cyan-500/10 p-5 rounded-xl grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
                      <div className="space-y-1">
                        <span className="text-[9px] font-mono text-slate-500 uppercase block font-bold">Estimated Taxable Base</span>
                        <strong className="text-lg font-mono text-white block">£{estimatedTaxableIncome.toLocaleString()}</strong>
                        <span className="text-[8px] font-mono text-slate-600 block uppercase">Non-exhaustive estimate logic</span>
                      </div>
                      <div className="space-y-1 border-t md:border-t-0 md:border-l border-white/5 md:pl-4">
                        <span className="text-[9px] font-mono text-slate-500 uppercase block font-bold">Claimed Expense Offsets</span>
                        <strong className="text-lg font-mono text-emerald-400 block">£{totalReimbursed.toLocaleString()}</strong>
                        <span className="text-[8px] font-mono text-emerald-500 block uppercase">Not deductible from dividends</span>
                      </div>
                      <div className="space-y-1 border-t md:border-t-0 md:border-l border-white/5 md:pl-4">
                        <span className="text-[9px] font-mono text-slate-500 uppercase block font-bold">Accountant Review Date</span>
                        <strong className="text-xs font-mono text-[#e840f3] block tracking-wider uppercase">REVIEW RECONCILING</strong>
                        <span className="text-[8px] font-mono text-slate-600 block uppercase">Continuous sync via Node-18.5</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* TAB: Director Income & Dividends register Screen 7 */}
              {activeSubTab === 'DIVIDENDS_REGISTER' && (
                <motion.div
                  key="dividends-register"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="flex justify-between items-center border-b border-white/5 pb-4">
                    <div>
                      <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                        <TrendingUp className="text-purple-400" size={18} /> Dividends & Director Income Register
                      </h4>
                      <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                        Tracked corporate payout events, board approvals, and shareholder dividend voucher registers.
                      </p>
                    </div>
                    <span className="px-2.5 py-1 rounded bg-black border border-white/10 text-[8.5px] font-mono text-[#00e5ff] tracking-widest uppercase">
                      YEAR 2025/2026 ACTIVE
                    </span>
                  </div>

                  <div className="overflow-x-auto border border-white/5 rounded-xl bg-zinc-950/40">
                    <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-900 bg-white/2 font-bold select-text">
                          <th className="p-3.5 text-zinc-400">Recipient / Director</th>
                          <th className="p-3.5 text-zinc-400 text-right">Director Salary (PAYE)</th>
                          <th className="p-3.5 text-zinc-400 text-right font-bold">Dividends Distributed</th>
                          <th className="p-3.5 text-zinc-400">Board Approval</th>
                          <th className="p-3.5 text-zinc-400">Director Loan Placement</th>
                          <th className="p-3.5 text-zinc-400">Audit Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        <tr className="hover:bg-white/2 transition-colors">
                          <td className="p-3.5">
                            <strong className="text-white block font-sans">Lead Director</strong>
                            <span className="text-[8px] text-purple-400 block uppercase">Director Node & CEO</span>
                          </td>
                          <td className="p-3.5 text-right text-slate-400 font-bold">£{directorSummary.directorSalary.toLocaleString()}</td>
                          <td className="p-3.5 text-right text-amber-400 font-black text-xs">£{directorSummary.dividendsReceived.toLocaleString()}</td>
                          <td className="p-3.5">
                            <span className="px-2 py-0.5 rounded text-[8px] font-extrabold uppercase bg-emerald-950/40 text-emerald-400 border border-emerald-500/20">
                              Approved & Sealed
                            </span>
                          </td>
                          <td className="p-3.5 text-slate-400 font-semibold">£{directorSummary.directorLoans}</td>
                          <td className="p-3.5">
                            <span className="text-amber-400 font-extrabold uppercase font-sans text-[8.5px]">
                              {directorSummary.accountantReviewStatus}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 p-3 bg-zinc-950/80 border border-amber-500/10 rounded-xl text-[10px] text-amber-300 font-mono flex items-start gap-2 text-left">
                    <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                      <strong>NEXORAX REVENUE ASSURANCE MANDATE:</strong> Dividend vouchers and monthly director loan indices must be meticulously vetted before tax filing or finalizing company accounts. Ensure minutes of meeting declarations are kept in the imperial Google Drive folder.
                    </div>
                  </div>
                </motion.div>
              )}

              {/* TAB: Payroll records vault Screen 4 */}
              {activeSubTab === 'PAYROLL_RECORDS' && (
                <motion.div
                  key="payroll-records"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="flex justify-between items-center border-b border-white/5 pb-4">
                    <div>
                      <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                        <Database className="text-purple-400" size={18} /> Payroll Records Vault
                      </h4>
                      <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                        Secured employee salary parameters, employer NI contributions, and PAYE allocations. Private by Default.
                      </p>
                    </div>
                    <span className="bg-[#e840f3]/15 border border-[#e840f3]/30 text-[#e840f3] px-2.5 py-1 rounded text-[8.5px] font-mono font-bold tracking-wider uppercase">
                      SECURED & METERED
                    </span>
                  </div>

                  {/* Table records */}
                  <div className="overflow-x-auto border border-white/5 rounded-xl bg-zinc-950/40">
                    <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-900 bg-white/2 font-bold">
                          <th className="p-3.5 text-zinc-400">Employee ID / Name</th>
                          <th className="p-3.5 text-zinc-400 select-all font-mono">Role / Commencement</th>
                          <th className="p-3.5 text-zinc-400 text-right">Gross Salary</th>
                          <th className="p-3.5 text-zinc-400 text-right">PAYE deducted</th>
                          <th className="p-3.5 text-zinc-400 text-right">NI Deducted</th>
                          <th className="p-3.5 text-zinc-400 text-right">Pension (Emp)</th>
                          <th className="p-3.5 text-zinc-400">Payroll Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {staffRecords.map((r) => (
                          <tr key={r.employeeId} className="hover:bg-white/2 transition-colors">
                            <td className="p-3.5">
                              <span className="text-white block font-sans font-black">{r.name}</span>
                              <span className="text-slate-550 block text-[8px] font-bold">{r.employeeId}</span>
                            </td>
                            <td className="p-3.5">
                              <span className="text-slate-300 block">{r.role}</span>
                              <span className="text-zinc-650 block text-[8px]">{r.startDate}</span>
                            </td>
                            <td className="p-3.5 text-right font-bold text-white">£{r.grossPay.toLocaleString()}</td>
                            <td className="p-3.5 text-right text-purple-300">£{r.payeTaxDeducted.toLocaleString()}</td>
                            <td className="p-3.5 text-right text-purple-400">£{r.niDeducted.toLocaleString()}</td>
                            <td className="p-3.5 text-right text-slate-400">
                              <span>£{r.pensionContributions}</span>
                              <span className="text-slate-600 block text-[8px]">Emp: £{r.employerPensionContributions}</span>
                            </td>
                            <td className="p-3.5">
                              <span className="px-2 py-0.5 rounded text-[8px] font-extrabold uppercase bg-emerald-950/40 text-emerald-400">
                                {r.payrollStatus}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-4 rounded-xl border border-white/5 bg-zinc-950/70 space-y-2 text-[10px] font-mono text-slate-400 text-left">
                    <span className="text-[#00e5ff] font-bold uppercase block tracking-wider">Super Admin Security Mandate:</span>
                    <p>
                      1. Sensitive payroll records must never be exposed publicly. Do not output National Insurance numbers or personal banking directions.
                    </p>
                    <p>
                      2. Real-time logging of user session metadata is active under the Google Drive NexoraX Resource Library.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* TAB: Statutory Document Tracker - Screen 5 */}
              {activeSubTab === 'TAX_TRACKER' && (
                <motion.div
                  key="statutory-tracker"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="border-b border-white/5 pb-4">
                    <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                      <FileCheck className="text-purple-400" size={18} /> P60 / P45 / P11D Tracking Matrix
                    </h4>
                    <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                      Statutory documents issued status, annual health benefit reporting summaries, and contractor allocations.
                    </p>
                  </div>

                  <div className="overflow-x-auto border border-white/5 rounded-xl bg-zinc-950/40">
                    <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-900 bg-white/2 font-bold">
                          <th className="p-3.5 text-zinc-400">Employee / Recipient</th>
                          <th className="p-3.5 text-zinc-400">Document Type</th>
                          <th className="p-3.5 text-zinc-400">Relevant Year</th>
                          <th className="p-3.5 text-zinc-400">Issue Status</th>
                          <th className="p-3.5 text-zinc-400 text-right">Stated Gross / Benefit Value</th>
                          <th className="p-3.5 text-zinc-400 text-right">Stated PAYE Tax</th>
                          <th className="p-3.5 text-zinc-400">Staff Downloaded</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {p60P45P11D.map((t) => (
                          <tr key={t.id} className="hover:bg-white/2 transition-colors">
                            <td className="p-3.5">
                              <strong className="text-white block font-sans">{t.employeeName}</strong>
                              <span className="text-[8px] text-slate-500 block font-bold">{t.employeeId}</span>
                            </td>
                            <td className="p-3.5 font-bold uppercase">{t.type}</td>
                            <td className="p-3.5">{t.year}</td>
                            <td className="p-3.5">
                              <span className={`px-2 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                                t.status === 'Issued' ? 'bg-emerald-950/40 text-emerald-400' : 'bg-amber-950/40 text-amber-500'
                              }`}>
                                {t.status}
                              </span>
                            </td>
                            <td className="p-3.5 text-right font-bold text-white">
                              {t.grossPay ? `£${t.grossPay.toLocaleString()}` : t.benefitDetails || "N/A"}
                            </td>
                            <td className="p-3.5 text-right text-purple-300">
                              {t.taxDeducted ? `£${t.taxDeducted.toLocaleString()}` : "N/A"}
                            </td>
                            <td className="p-3.5">
                              <span className={`px-2 py-0.5 rounded text-[8px] font-bold ${
                                t.downloadedByStaff === 'Yes' ? 'bg-indigo-950/40 text-indigo-400' : 'bg-slate-900 text-slate-500'
                              }`}>
                                {t.downloadedByStaff}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-3.5 rounded-xl border border-purple-500/20 bg-purple-950/15 text-[10px] text-purple-300/90 font-mono leading-relaxed text-left">
                    <strong>P11D BENEFIT DISCLOSURE WARNING:</strong> Use this system as a records-tracking and monitoring matrix only. Never attempt to submit P11D forms or benefit parameters unless proper HMRC payroll gateway and secure API authorizations are completely initialized on our servers.
                  </div>
                </motion.div>
              )}

              {/* TAB: Staff Tax Readiness Index - Screen 3 (Admin side view) */}
              {activeSubTab === 'STAFF_INDEX' && (
                <motion.div
                  key="staff-index"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="flex justify-between items-center border-b border-white/5 pb-4">
                    <div>
                      <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                        <Users className="text-purple-400" size={18} /> Staff Tax Readiness Index
                      </h4>
                      <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                        Admin utility tracking document issue flags for permanent staff members and remote nodes.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {staffRecords.map((emp) => (
                      <div key={emp.employeeId} className="bg-black/40 border border-white/5 rounded-xl p-5 space-y-4 text-left">
                        <div className="flex justify-between items-start border-b border-white/5 pb-3">
                          <div>
                            <h5 className="font-sans font-black text-white">{emp.name}</h5>
                            <span className="text-[8px] font-mono text-purple-400 uppercase tracking-widest font-bold">
                              {emp.role} // {emp.employeeId}
                            </span>
                          </div>
                          <span className="text-[8px] font-mono bg-[#00e5ff]/10 border border-[#00e5ff]/20 px-2 py-0.5 rounded text-[#00e5ff] font-bold uppercase tracking-wider">
                            Sync Staged
                          </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 font-mono text-[9px]">
                          <div>
                            <span className="text-slate-500 uppercase block">P60 Issued:</span>
                            <span className="text-slate-200 font-extrabold">{emp.p60Issued}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 uppercase block">P45 Required (Leaver):</span>
                            <span className="text-slate-200 font-extrabold">{emp.p45Issued}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 uppercase block">P11D Filing Required:</span>
                            <span className="text-slate-200 font-extrabold">{emp.p11dRequired}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 uppercase block">P11D Status:</span>
                            <span className="text-amber-500 font-bold">{emp.p11dStatus}</span>
                          </div>
                        </div>

                        <div className="pt-2 border-t border-white/5 flex gap-2 justify-end">
                          <button 
                            onClick={() => handleAuditReviewStatus("P11D", emp.employeeId, "Under Review")}
                            className="px-2.5 py-1 bg-purple-900/40 hover:bg-purple-900/60 text-purple-300 text-[8.5px] rounded border border-purple-500/30 uppercase tracking-widest font-bold font-mono cursor-pointer"
                          >
                            Mark Reviewed
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-3 bg-zinc-950/80 border border-white/5 rounded-xl text-[10px] text-slate-500 font-mono text-left leading-relaxed">
                    <span className="text-white block font-bold mb-1">STAFF SELF ASSESSMENT DIRECTIONAL MANDATE:</span>
                    Most permanent UK staff tax liability is seamlessly reconciled through cumulative PAYE. Staff Self-Assessment is only mandatory under personal situations or auxiliary revenue channels. Nexus provides summaries and structural forms tracking, not direct legal or tax advisory assistance.
                  </div>
                </motion.div>
              )}

              {/* TAB: Expenses & Benefits Screen 6 */}
              {activeSubTab === 'EXPENSES_BENEFITS' && (
                <motion.div
                  key="expenses-benefits"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                    <div>
                      <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                        <CreditCard className="text-purple-400" size={18} /> Expenses & Taxable Benefits Register
                      </h4>
                      <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                        Categorized business operational expense claims, mileage offsets, and taxable employee fringe benefits.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAddExpense(!showAddExpense)}
                      className="px-3.5 py-2 bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 border border-purple-500/40 rounded-xl text-[10px] font-mono font-bold text-white uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1"
                    >
                      <Plus size={12} /> Claim Expense
                    </button>
                  </div>

                  {/* Add expense sub-form block */}
                  {showAddExpense && (
                    <form onSubmit={handleAddExpenseSubmit} className="bg-black/60 border border-purple-500/30 p-5 rounded-xl space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Claimant Name</label>
                          <input 
                            type="text" 
                            value={newExpense.employeeName}
                            onChange={(e) => setNewExpense({ ...newExpense, employeeName: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Expense Class</label>
                          <select 
                            value={newExpense.expenseType}
                            onChange={(e) => setNewExpense({ ...newExpense, expenseType: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                          >
                            <option value="Travel">Travel</option>
                            <option value="Mileage">Mileage</option>
                            <option value="Subsistence">Subsistence</option>
                            <option value="Home office">Home office</option>
                            <option value="Equipment">Equipment</option>
                            <option value="Software">Software</option>
                            <option value="Phone/internet">Phone/internet</option>
                            <option value="Training">Training</option>
                            <option value="Professional fees">Professional fees</option>
                            <option value="Company car placeholder">Company car</option>
                            <option value="Health/insurance placeholder">Health Insurance</option>
                            <option value="Other benefit">Other Benefit</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Claim Value (£)</label>
                          <input 
                            type="number" 
                            placeholder="0.00"
                            value={newExpense.amount}
                            onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Business Purpose Note</label>
                          <input 
                            type="text" 
                            placeholder="State justification..."
                            value={newExpense.purpose}
                            onChange={(e) => setNewExpense({ ...newExpense, purpose: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Benefit Treatment Category</label>
                          <select 
                            value={newExpense.benefitType}
                            onChange={(e) => setNewExpense({ ...newExpense, benefitType: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                          >
                            <option value="Other benefit">Other Benefit (Default)</option>
                            <option value="Company car placeholder">Company Car Scheme</option>
                            <option value="Health/insurance placeholder">Health/Insurance Scheme</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex gap-2 justify-end">
                        <button 
                          type="button" 
                          onClick={() => setShowAddExpense(false)}
                          className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-850 rounded-lg text-slate-400 text-[10px] font-mono font-bold uppercase cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="px-4 py-1.5 bg-purple-600 hover:bg-purple-500 border border-purple-500 text-white text-[10px] font-mono font-bold uppercase rounded-lg cursor-pointer"
                        >
                          Commit Claim
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="overflow-x-auto border border-white/5 rounded-xl bg-zinc-950/40">
                    <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-900 bg-white/2 font-bold">
                          <th className="p-3.5 text-zinc-400">Claimant</th>
                          <th className="p-3.5 text-zinc-400 col-span-2">Date / Class</th>
                          <th className="p-3.5 text-zinc-400 text-right">Sum Claimed</th>
                          <th className="p-3.5 text-zinc-400">Treatment Class</th>
                          <th className="p-3.5 text-zinc-400">Verification</th>
                          <th className="p-3.5 text-zinc-400 text-right">Review Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {expenses.map((e) => (
                          <tr key={e.id} className="hover:bg-white/2 transition-colors">
                            <td className="p-3.5">
                              <strong className="text-white block font-sans">{e.employeeName}</strong>
                              <span className="text-[8px] text-slate-500 block uppercase font-bold">{e.purpose}</span>
                            </td>
                            <td className="p-3.5 col-span-2">
                              <span className="px-1.5 py-0.5 rounded text-[8px] font-bold bg-cyan-950 text-cyan-400 border border-cyan-800/40">
                                {e.expenseType}
                              </span>
                              <span className="text-slate-500 block text-[8px] mt-1">{e.date}</span>
                            </td>
                            <td className="p-3.5 text-right text-white font-extrabold">£{e.amount.toLocaleString()}</td>
                            <td className="p-3.5 text-slate-450">
                              <div className="text-[9px] font-bold text-purple-300">{e.payrollTreatment}</div>
                              <div className="text-[8px] text-slate-500 font-bold block uppercase">{e.p11DTreatment}</div>
                            </td>
                            <td className="p-3.5 text-slate-400">
                              <div className="text-[9px]">Receipt: <span className="text-emerald-400 font-bold">{e.receiptAttached}</span></div>
                              <div className="text-[8px] text-slate-500 block font-bold">{e.taxableStatus}</div>
                            </td>
                            <td className="p-3.5 text-right">
                              <div className="flex flex-col items-end gap-1">
                                <span className={`px-2 py-0.5 rounded text-[8px] font-bold ${
                                  e.accountantReviewStatus === 'Completed' ? 'bg-emerald-950 text-emerald-400' : 'bg-amber-950 text-amber-500'
                                }`}>
                                  {e.accountantReviewStatus}
                                </span>
                                {e.accountantReviewStatus !== 'Completed' && (
                                  <button
                                    onClick={() => handleAuditReviewStatus("EXPENSE", e.id, "Completed")}
                                    className="text-[8px] text-purple-400 hover:text-purple-300 underline uppercase block font-bold cursor-pointer"
                                  >
                                    Verify
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-3 rounded-xl bg-[#02050e]/60 border border-white/5 text-[10px] text-slate-650 font-mono text-left leading-relaxed">
                    <AlertTriangle className="w-4 h-4 text-purple-500 shrink-0 inline mr-2" />
                    <strong>EXPENSES COMPLIANCE ADVISORY:</strong> Verified expenses claims and corporate mileage allocations have strict National Insurance and P11D implications. Professional accountant review is mandatory before submitting company accounts.
                  </div>
                </motion.div>
              )}

              {/* TAB: Contractor & Freelancer Records - Screen 8 */}
              {activeSubTab === 'CONTRACTORS' && (
                <motion.div
                  key="contractor-records"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                    <div>
                      <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                        <Briefcase className="text-purple-400" size={18} /> Contractor & Freelancer Registers
                      </h4>
                      <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                        Tracked third-party independent consultants, statement of work invoices, and IR35 review status indices.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAddContractor(!showAddContractor)}
                      className="px-3.5 py-2 bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 border border-purple-500/40 rounded-xl text-[10px] font-mono font-bold text-white uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1"
                    >
                      <Plus size={12} /> Add Contractor Record
                    </button>
                  </div>

                  {/* Add contractor record sub-form block */}
                  {showAddContractor && (
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const payload = {
                        id: `CON-${String(contractors.length + 101).padStart(3, '0')}`,
                        contractorName: newContractor.contractorName,
                        businessName: newContractor.businessName,
                        invoiceNo: newContractor.invoiceNo || "NEX-INV-99",
                        serviceProvided: newContractor.serviceProvided,
                        amount: Number(newContractor.amount),
                        paymentStatus: "Pending",
                        taxStatus: newContractor.taxStatus,
                        contractStatus: "Active",
                        ir35ReviewPlaceholder: "Outside IR35 (NexoraX B2B SLA)",
                        accountantReviewStatus: "Review Pending"
                      };
                      setContractors([...contractors, payload]);
                      setShowAddContractor(false);
                      setNewContractor({
                        contractorName: "",
                        businessName: "",
                        invoiceNo: "",
                        serviceProvided: "",
                        amount: "",
                        taxStatus: "Gross Payment"
                      });
                      showNotification("Contractor ledger record stored", "success");
                    }} className="bg-black/60 border border-purple-500/30 p-5 rounded-xl space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Contractor Name</label>
                          <input 
                            type="text" 
                            value={newContractor.contractorName}
                            onChange={(e) => setNewContractor({ ...newContractor, contractorName: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Company / Entity Name</label>
                          <input 
                            type="text" 
                            value={newContractor.businessName}
                            onChange={(e) => setNewContractor({ ...newContractor, businessName: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Invoice Value (£)</label>
                          <input 
                            type="number" 
                            placeholder="0.00"
                            value={newContractor.amount}
                            onChange={(e) => setNewContractor({ ...newContractor, amount: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Service Provided</label>
                          <input 
                            type="text" 
                            placeholder="Technical description..."
                            value={newContractor.serviceProvided}
                            onChange={(e) => setNewContractor({ ...newContractor, serviceProvided: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Tax treatment</label>
                          <select 
                            value={newContractor.taxStatus}
                            onChange={(e) => setNewContractor({ ...newContractor, taxStatus: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                          >
                            <option value="Gross Payment">Gross Payment (B2B)</option>
                            <option value="PAYE Retained">PAYE Retained (Inside IR35)</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex gap-2 justify-end">
                        <button 
                          type="button" 
                          onClick={() => setShowAddContractor(false)}
                          className="px-3 py-1.5 bg-zinc-900 hover:bg-zinc-850 rounded-lg text-slate-400 text-[10px] font-mono font-bold uppercase cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="px-4 py-1.5 bg-purple-600 hover:bg-purple-500 border border-purple-500 text-white text-[10px] font-mono font-bold uppercase rounded-lg cursor-pointer"
                        >
                          Commit Record
                        </button>
                      </div>
                    </form>
                  )}

                  <div className="overflow-x-auto border border-white/5 rounded-xl bg-zinc-950/40">
                    <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-900 bg-white/2 font-bold">
                          <th className="p-3.5 text-zinc-400">Consultant / Business</th>
                          <th className="p-3.5 text-zinc-400">SOW Deliverables</th>
                          <th className="p-3.5 text-zinc-400 text-right">Sum Invoice</th>
                          <th className="p-3.5 text-zinc-400">Payment status</th>
                          <th className="p-3.5 text-zinc-400">IR35 Audit Checklist</th>
                          <th className="p-3.5 text-zinc-400 text-right">Review Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {contractors.map((c) => (
                          <tr key={c.id} className="hover:bg-white/2 transition-colors">
                            <td className="p-3.5">
                              <strong className="text-white block font-sans">{c.contractorName}</strong>
                              <span className="text-[8px] text-cyan-400 block uppercase font-bold">{c.businessName}</span>
                            </td>
                            <td className="p-3.5">
                              <span className="text-slate-300 block">{c.serviceProvided}</span>
                              <span className="text-zinc-650 block text-[8px] font-bold">INV: {c.invoiceNo}</span>
                            </td>
                            <td className="p-3.5 text-right font-bold text-white">£{c.amount.toLocaleString()}</td>
                            <td className="p-3.5">
                              <span className={`px-2 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                                c.paymentStatus === 'Paid' ? 'bg-emerald-950 text-emerald-400 border border-emerald-800/35' : 'bg-yellow-950 text-yellow-400'
                              }`}>
                                {c.paymentStatus}
                              </span>
                            </td>
                            <td className="p-3.5 text-slate-450">
                              <div className="text-[9px] text-[#00ffd2] font-black">{c.ir35ReviewPlaceholder}</div>
                              <div className="text-[8px] text-zinc-550 block uppercase font-bold">{c.taxStatus}</div>
                            </td>
                            <td className="p-3.5 text-right">
                              <div className="flex flex-col items-end gap-1">
                                <span className={`px-2 py-0.5 rounded text-[8px] font-bold ${
                                  c.accountantReviewStatus === 'Completed' ? 'bg-emerald-950 text-emerald-400' : 'bg-amber-950 text-amber-500'
                                }`}>
                                  {c.accountantReviewStatus}
                                </span>
                                {c.accountantReviewStatus !== 'Completed' && (
                                  <button
                                    onClick={() => handleAuditReviewStatus("CONTRACTOR", c.id, "Completed")}
                                    className="text-[8px] text-purple-400 hover:text-purple-300 underline uppercase block font-bold cursor-pointer"
                                  >
                                    Mark Verified
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-3 bg-zinc-950/80 border border-yellow-500/10 rounded-xl text-[10px] text-yellow-400/90 font-mono text-left leading-relaxed flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 text-yellow-500 shrink-0 mt-0.5" />
                    <div>
                      <strong>UK IR35 OFF-WESTMINSTER WORKER REGULATION ASSURANCE:</strong> Tax treatment for contractor payments requires thorough assessment based on B2B contractual realities and working practices. Professional validation ensures we mitigate HMRC compliance exposure.
                    </div>
                  </div>
                </motion.div>
              )}

              {/* TAB: Deadlines & Reminders Screen 9 & Screen 13 Reminders */}
              {activeSubTab === 'DEADLINES_TRACK' && (
                <motion.div
                  key="deadlines-track"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="border-b border-white/5 pb-4">
                    <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                      <Clock className="text-purple-400" size={18} /> Tax Deadlines & Payroll Reminders Panel
                    </h4>
                    <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                      Urgent compliance milestones, monthly payroll run directives, and professional accountant coordination milestones.
                    </p>
                  </div>

                  {/* Highlight Payroll reminders row Screen 13 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    
                    {/* Column 1: Compliance deadlines checklist */}
                    <div className="space-y-4">
                      <h5 className="text-xs font-mono font-black text-amber-500 uppercase tracking-widest border-l-2 border-amber-500 pl-2">
                        Tax Milestones Calendar
                      </h5>
                      <div className="space-y-3">
                        {deadlines.map((dl) => (
                          <div key={dl.id} className="bg-black/30 border border-white/5 p-3.5 rounded-xl flex justify-between items-center text-left">
                            <div className="space-y-1">
                              <span className="text-[10px] font-sans font-black text-white block">{dl.taskName}</span>
                              <span className="text-[9px] font-mono text-slate-500 block uppercase font-bold">
                                Due Limit: {dl.deadline}
                              </span>
                            </div>
                            <div className="flex flex-col items-end gap-1.5">
                              <span className={`px-2 py-0.5 rounded text-[8px] font-mono font-bold uppercase tracking-wider ${
                                dl.status === 'Completed' ? 'bg-emerald-950/40 text-emerald-450 border border-emerald-800/40' :
                                dl.status === 'Due soon' ? 'bg-amber-950/40 text-amber-400 border border-amber-800/40' :
                                'bg-rose-950/40 text-rose-400 border border-rose-800/40'
                              }`}>
                                {dl.status}
                              </span>
                              <span className="text-[8px] text-slate-500 font-mono">
                                Acc: <strong className="text-purple-400">{dl.accountantReviewRequired}</strong>
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Column 2: Monthly internal reminders panel Screen 13 */}
                    <div className="space-y-4">
                      <h5 className="text-xs font-mono font-black text-cyan-400 uppercase tracking-widest border-l-2 border-cyan-400 pl-2">
                        Payroll & PAYE Reminder Hub
                      </h5>
                      
                      <div className="space-y-3 font-mono text-[9.5px] text-slate-350">
                        <div className="p-3.5 rounded-xl bg-purple-950/15 border border-purple-500/20 text-left space-y-2">
                          <strong className="text-white block uppercase text-[10px] tracking-wide">Monthly Payroll Run Checklist</strong>
                          <div className="flex items-center gap-2">
                            <CheckCircle size={12} className="text-emerald-400 shrink-0" />
                            <span>Retrieve employee timesheets and clock blocks</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <CheckCircle size={12} className="text-emerald-400 shrink-0" />
                            <span>Verify HMRC tax code modifications</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AlertCircle size={12} className="text-amber-500 shrink-0" />
                            <span>Calculate PAYE/NI obligations manually for reviewer</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AlertCircle size={12} className="text-purple-400 shrink-0" />
                            <span>Generate draft payslips in PDF vault (EMP-001 / EMP-002)</span>
                          </div>
                        </div>

                        <div className="p-3.5 rounded-xl bg-[#02050e]/80 border border-white/5 text-left space-y-2 leading-relaxed">
                          <strong className="text-[#00e5ff] block uppercase tracking-wider text-[9px]">SYSTEM COMPLIANCE NOTICE:</strong>
                          This reminders and calendar utility does not submit payroll returns or PAYE statistics. Submitting payroll parameters to HMRC requires approved third-party filing software or an expert manual process supervised by our accountant.
                        </div>
                      </div>
                    </div>

                  </div>
                </motion.div>
              )}

              {/* TAB: Document Vault Screen 10 & Screen 12 Staff logs */}
              {activeSubTab === 'DOCUMENT_VAULT' && (
                <motion.div
                  key="document-vault"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-white/5 pb-4">
                    <div>
                      <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                        <Lock className="text-purple-400" size={18} /> Tax Document Vault & Access Matrix
                      </h4>
                      <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                        Double-locked document storage logs. Permanent staff members have read-only access to their own parameters.
                      </p>
                    </div>
                    {['SUPER_ADMIN', 'ADMIN'].includes(activeRole) && (
                      <button
                        onClick={() => setShowAddDoc(!showAddDoc)}
                        className="px-3.5 py-2 bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-500 hover:to-purple-700 border border-purple-500/40 rounded-xl text-[10px] font-mono font-bold text-white uppercase tracking-widest transition-all cursor-pointer flex items-center gap-1"
                      >
                        <Upload size={12} /> Secure Upload
                      </button>
                    )}
                  </div>

                  {showAddDoc && (
                    <form onSubmit={handleAddDocSubmit} className="bg-black/60 border border-purple-500/30 p-5 rounded-xl space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Document Name</label>
                          <input 
                            type="text" 
                            placeholder="e.g. P60_2026.pdf"
                            value={newDoc.name}
                            onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                            required
                          />
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Doc Type</label>
                          <select 
                            value={newDoc.type}
                            onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                          >
                            <option value="Payslip">Payslip</option>
                            <option value="P60">P60</option>
                            <option value="P45">P45</option>
                            <option value="P11D">P11D</option>
                            <option value="Dividend voucher">Dividend voucher</option>
                            <option value="Expense receipt">Expense receipt</option>
                            <option value="Contractor invoice">Contractor invoice</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-[9px] font-mono text-slate-400 uppercase tracking-widest block font-bold mb-1">Owner Assignment ID</label>
                          <select 
                            value={newDoc.ownerId}
                            onChange={(e) => setNewDoc({ ...newDoc, ownerId: e.target.value })}
                            className="w-full h-9 px-3 bg-zinc-950 border border-zinc-800 text-slate-350 font-mono text-xs rounded-lg focus:outline-none focus:border-purple-500"
                          >
                            <option value="EMP-001">EMP-001 (Staff Coordinator)</option>
                            <option value="EMP-002">EMP-002 (Communications Associate Parween)</option>
                            <option value="DIRECTOR">DIRECTOR (Lead Director)</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex gap-2 justify-end font-mono">
                        <button 
                          type="button" 
                          onClick={() => setShowAddDoc(false)}
                          className="px-3 py-1.5 bg-zinc-900 text-slate-400 text-[10px] uppercase font-bold cursor-pointer rounded"
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="px-4 py-1.5 bg-purple-600 border border-purple-500 text-white text-[10px] uppercase font-bold cursor-pointer rounded"
                        >
                          Lock Upload
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Document layout table Screen 10 */}
                  <div className="overflow-x-auto border border-white/5 rounded-xl bg-zinc-950/40">
                    <table className="w-full text-left font-mono text-[10px] leading-relaxed border-collapse">
                      <thead>
                        <tr className="border-b border-zinc-900 bg-white/2 font-bold">
                          <th className="p-3.5 text-zinc-400">Document ID / File Name</th>
                          <th className="p-3.5 text-zinc-400 font-bold">Type</th>
                          <th className="p-3.5 text-zinc-400">Assigned ID</th>
                          <th className="p-3.5 text-zinc-400">Size</th>
                          <th className="p-3.5 text-zinc-400">Indexed Restriction</th>
                          <th className="p-3.5 text-zinc-400 text-right">Download Handshake</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-900">
                        {taxDocuments.map((doc) => (
                          <tr key={doc.id} className="hover:bg-white/2 transition-colors">
                            <td className="p-3.5">
                              <span className="text-[#00ffd2] block font-bold">{doc.name}</span>
                              <span className="text-zinc-650 block text-[8px] font-bold">Locked ID: {doc.id} // Date: {doc.uploadDate}</span>
                            </td>
                            <td className="p-3.5">
                              <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-purple-950 text-purple-400 border border-purple-800">
                                {doc.type}
                              </span>
                            </td>
                            <td className="p-3.5 font-bold text-white">{doc.ownerId}</td>
                            <td className="p-3.5 font-semibold text-slate-400">{doc.size}</td>
                            <td className="p-3.5">
                              <span className="px-2 py-0.5 rounded text-[8px] font-bold bg-slate-900 text-slate-500 border border-slate-800 uppercase tracking-widest font-mono">
                                NO SEARCH INDEX
                              </span>
                            </td>
                            <td className="p-3.5 text-right font-bold text-white">
                              <button 
                                onClick={() => showNotification("Secure download initiated.", "success")}
                                className="px-2 py-1 bg-cyan-950 hover:bg-cyan-900 border border-cyan-800 text-cyan-400 text-[8px] uppercase tracking-widest font-mono rounded cursor-pointer font-bold"
                              >
                                Decrypt Pack
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Staff privacy audit logs screen 12 */}
                  {['SUPER_ADMIN', 'ADMIN'].includes(activeRole) && (
                    <div className="space-y-3">
                      <h5 className="text-[10.5px] font-mono font-bold text-slate-400 uppercase tracking-widest border-b border-white/5 pb-2">
                        Staff Privacy & Access Audit Logs (Screen 12)
                      </h5>
                      <div className="max-h-[140px] overflow-y-auto space-y-2 border border-white/5 p-3 rounded-xl bg-black/40 scrollbar-thin">
                        {auditLogs.map((log, i) => (
                          <div key={i} className="text-[9px] font-mono text-slate-400 flex flex-wrap justify-between items-center py-1.5 border-b border-zinc-900/60 leading-relaxed text-left">
                            <div className="space-y-0.5">
                              <span>User: <strong className="text-white">{log.userId}</strong> accessed <strong className="text-cyan-400">{log.recordType}</strong> ({log.recordId})</span>
                              <span className="text-zinc-650 block">Time: {log.accessTime} // action: {log.action}</span>
                            </div>
                            <span className="text-[8px] font-extrabold bg-[#022312] border border-emerald-500/20 px-2 py-0.5 rounded text-emerald-400 uppercase tracking-wider">
                              {log.approvalStatus}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="p-3 rounded-xl bg-zinc-950/80 border border-white/5 text-[9px] font-mono text-slate-700 text-left leading-relaxed">
                    <span className="text-white block font-bold text-[9.5px]">STAFF COMPLIANCE INTEGRITY RULEBOOK:</span>
                    Strict private-by-default access control is enforced. LocalStorage caching of sensitive documents is disabled.
                  </div>
                </motion.div>
              )}

              {/* TAB: Personal Staff-View Area - Screen 3 & Privacy requests */}
              {activeSubTab === 'STAFF_COMP' && (
                <motion.div
                  key="staff-comp-view"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6 animate-pulse-slow"
                >
                  <div className="flex justify-between items-center border-b border-white/5 pb-4">
                    <div>
                      <h4 className="text-base font-black uppercase text-white tracking-widest">
                        My Personal Tax & Payroll Status Portal
                      </h4>
                      <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                        Access control level: <strong className="text-emerald-400">STAFF NODE AUTHORIZED</strong>
                      </p>
                    </div>
                    <span className="bg-emerald-950 text-emerald-400 border border-emerald-800 px-2.5 py-1 rounded text-[8.5px] font-mono font-bold tracking-wider uppercase">
                      SECURED HANDSHAKE
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                    {/* Panel 1: Personal tax checklist & benefits */}
                    <div className="bg-black/40 border border-white/5 p-5 rounded-xl space-y-4">
                      <h5 className="text-[10px] font-mono font-black text-cyan-400 uppercase tracking-widest border-l-2 border-cyan-400 pl-2">
                        Issued Records Summary
                      </h5>
                      
                      <div className="space-y-2.5 font-mono text-[9.5px] text-slate-350">
                        <div className="flex justify-between">
                          <span>Verified Tax Code:</span>
                          <span className="text-white font-extrabold font-mono">1257L (Simulated)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>P60 Issuance Status:</span>
                          <span className="text-emerald-400 font-extrabold font-mono">ISSUED (Tax Year 21/22/23/24/25)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>P45 Status:</span>
                          <span className="text-slate-500 font-extrabold font-mono">N/A (Active permanent)</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Reimbursed Expenses:</span>
                          <span className="text-white font-extrabold font-mono">All claims cleared and settled</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Annual Benefit summary:</span>
                          <span className="text-slate-400 font-mono">Company Phone line & basic coverage health plan</span>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-white/5 space-y-1">
                        <span className="text-[8.5px] text-slate-550 block font-bold font-mono">PAYROLL INGRESS POINT:</span>
                        <div className="text-[9.5px] font-mono text-slate-400">
                          For payslip queries or salary disputes, contact corporate ledger node admin at <strong className="text-purple-300">support@nexoraxdigital.co.uk</strong>.
                        </div>
                      </div>
                    </div>

                    {/* Panel 2: Secure document downloads Screen 3 */}
                    <div className="bg-black/40 border border-white/5 p-5 rounded-xl space-y-4 flex flex-col justify-between">
                      <div>
                        <h5 className="text-[10px] font-mono font-black text-purple-400 uppercase tracking-widest border-l-2 border-purple-400 pl-2">
                          My Secure Tax Documents
                        </h5>
                        <p className="text-[8.5px] font-mono text-slate-550 uppercase mt-1">
                          Private tax year summaries issued to EMP-001 by NexoraX Admin control plane.
                        </p>

                        <div className="space-y-2 mt-4 font-mono text-xs">
                          <button 
                            onClick={() => showNotification("Initiating encryption pass on P60_2025_2026_Staff Coordinator.pdf...", "success")}
                            className="w-full py-2 px-3 bg-zinc-950 hover:bg-zinc-900 border border-zinc-800 text-slate-300 rounded-lg flex items-center justify-between text-[10px] cursor-pointer"
                          >
                            <span>P60_2025_2026.pdf (340 KB)</span>
                            <Download className="w-3.5 h-3.5 text-purple-400" />
                          </button>
                        </div>
                      </div>

                      {/* Privacy request link screen 12 */}
                      <div className="pt-3 border-t border-white/5 text-[9px] font-mono">
                        <span className="text-[#00e5ff] font-bold block mb-1">PRIVACY COMPLIANCE CONTROLS:</span>
                        <button 
                          type="button"
                          onClick={() => showNotification("Privacy request recorded. Chief Compliance Officer will evaluate raw log purges.", "success")}
                          className="text-purple-400 hover:text-purple-300 underline font-black uppercase text-left cursor-pointer"
                        >
                          Request Personal Access Log Purge / GDPR Right to be Forgotten
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* TAB: Accountant Export Centre Screen 11 */}
              {activeSubTab === 'EXPORTS_CENTRE' && (
                <motion.div
                  key="exports-centre"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-purple-950/10 border border-purple-500/20 backdrop-blur-md rounded-2xl p-6 space-y-6"
                >
                  <div className="border-b border-white/5 pb-4">
                    <h4 className="text-base font-black uppercase text-white tracking-widest flex items-center gap-2">
                      <Download className="text-[#00ffd2]" size={18} /> Accountant Self Assessment Export Centre
                    </h4>
                    <p className="text-[10px] font-mono text-slate-400 uppercase mt-0.5">
                      Compile complete dataset packages, SHA256 integrity check blocks, and PDF balance checklists for corporate accountant filing review.
                    </p>
                  </div>

                  {/* Compile buttons layout */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="p-4 bg-black/40 border border-white/5 rounded-xl text-left space-y-3">
                      <span className="text-[10px] font-mono text-[#00e5ff] uppercase block font-bold">1. Director Assessment Pack</span>
                      <p className="text-[8.5px] text-slate-500 font-mono">Includes dividend distributions, director salaries, board minutes registers.</p>
                      <button 
                        onClick={() => triggerAccountantExport('DIRECTOR_PACK')}
                        className="w-full py-1.5 bg-purple-900/40 hover:bg-purple-900/60 text-purple-300 text-[9px] font-mono font-black border border-purple-500/30 rounded uppercase tracking-wider cursor-pointer"
                      >
                        Compile Pack
                      </button>
                    </div>

                    <div className="p-4 bg-black/40 border border-white/5 rounded-xl text-left space-y-3">
                      <span className="text-[10px] font-mono text-[#e840f3] uppercase block font-bold">2. Staff Payroll & P60/P45</span>
                      <p className="text-[8.5px] text-slate-500 font-mono font-thin">Includes permanent staff taxable pay aggregates, issued statutory files.</p>
                      <button 
                        onClick={() => triggerAccountantExport('STAFF_PAYROLL_PACK')}
                        className="w-full py-1.5 bg-purple-900/40 hover:bg-purple-900/60 text-purple-300 text-[9px] font-mono font-black border border-purple-500/30 rounded uppercase tracking-wider cursor-pointer"
                      >
                        Compile Pack
                      </button>
                    </div>

                    <div className="p-4 bg-black/40 border border-white/5 rounded-xl text-left space-y-3">
                      <span className="text-[10px] font-mono text-amber-400 uppercase block font-bold">3. Full Audit Core Assembly</span>
                      <p className="text-[8.5px] text-zinc-500 font-mono">Consolidates expenses, subcontractor invoices, IR35 trackers, and document checklist indices.</p>
                      <button 
                        onClick={() => triggerAccountantExport('FULL_ASSEMBLY')}
                        className="w-full py-2 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 font-mono text-white text-[9px] font-extrabold rounded uppercase tracking-wider cursor-pointer"
                      >
                        Compile Master Assembly
                      </button>
                    </div>
                  </div>

                  {/* Render simulated compiled export output pack */}
                  {exportData && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-[#02050e] border border-cyan-500/20 p-5 rounded-xl space-y-4 text-left"
                    >
                      <div className="flex justify-between items-center border-b border-white/5 pb-3">
                        <span className="text-xs font-mono font-black text-cyan-400 uppercase tracking-widest">
                          COMPILED ACCOUNTANT AUDIT SUMMARY (ACTIVE PREVIEW)
                        </span>
                        <div className="flex gap-2 font-mono text-[8px] tracking-widest uppercase">
                          <button 
                            onClick={() => showNotification("XLSX grid model compiled in memory.", "success")} 
                            className="bg-zinc-950 hover:bg-zinc-900 px-2 py-1 rounded text-slate-400 cursor-pointer border border-zinc-800"
                          >
                            XLSX
                          </button>
                          <button 
                            onClick={() => showNotification("CSV data spreadsheet assembled.", "success")} 
                            className="bg-zinc-950 hover:bg-zinc-900 px-2 py-1 rounded text-slate-400 cursor-pointer border border-zinc-800"
                          >
                            CSV
                          </button>
                          <button 
                            onClick={() => showNotification("JSON ledger array exported.", "success")} 
                            className="bg-zinc-950 hover:bg-zinc-900 px-2 py-1 rounded text-slate-400 cursor-pointer border border-zinc-800"
                          >
                            JSON
                          </button>
                          <button 
                            onClick={() => showNotification("PDF audit summary compilation completed.", "success")} 
                            className="bg-[#00ffd2]/10 hover:bg-[#00ffd2]/25 px-2.5 py-1 rounded text-[#00ffd2] border border-[#00ffd2]/30 cursor-pointer"
                          >
                            PDF SUMMARY
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 font-mono text-[9px]">
                        <div>
                          <span className="text-slate-500 uppercase block">Selected Tax Year:</span>
                          <span className="text-zinc-200 font-extrabold">{exportData.exportMetadata.taxYear}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 uppercase block">Timestamp Generated:</span>
                          <span className="text-zinc-200 font-extrabold">{exportData.exportMetadata.timestamp}</span>
                        </div>
                        <div>
                          <span className="text-slate-500 uppercase block">Auditable Ledger Hash:</span>
                          <span className="text-amber-400 font-extrabold overflow-hidden truncate max-w-[120px] block font-mono">
                            {exportData.exportMetadata.integrityHash}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-500 uppercase block">Authorized Compiler:</span>
                          <span className="text-zinc-200 font-extrabold">{exportData.exportMetadata.generatedBy}</span>
                        </div>
                      </div>

                      <div className="pt-2 border-t border-white/5 text-[9px] font-mono text-slate-400 leading-relaxed uppercase">
                        <span className="text-yellow-400 block font-bold mb-0.5">ACCOUNTANT FILING DISCLAIMER:</span>
                        This compiled export package serves as an audit preparation file only. This package does not constitute a filed Self-Assessment form, PAYE layout statistics, Corporation Tax, or VAT statement to HMRC.
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              )}

            </AnimatePresence>
          )}

        </div>
      </div>

      {/* Review preview state panel (Screen 14) */}
      <div className="bg-black/60 border border-white/5 rounded-2xl p-5 text-left grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
        <div className="md:col-span-2">
          <h4 className="text-xs font-mono font-black text-white uppercase tracking-widest">
            Nexus Governance Review Checklist Status (Screen 14)
          </h4>
          <p className="text-[9.5px] font-mono text-slate-500 mt-1 uppercase">
            Internal administrative monitoring gatekeeping and status logs checks.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 whitespace-nowrap text-[9px] font-mono leading-none md:col-span-2">
          <div className="p-2.5 rounded-lg bg-[#022312]/20 border border-emerald-500/20">
            <span className="text-slate-500 uppercase block text-[8px] mb-1">Director Income Review</span>
            <strong className="text-emerald-400 flex items-center gap-1">
              <CheckCircle size={10} className="text-emerald-400" /> {directorSummary.accountantReviewStatus}
            </strong>
          </div>
          <div className="p-2.5 rounded-lg bg-zinc-950/80 border border-zinc-900">
            <span className="text-slate-500 uppercase block text-[8px] mb-1">Compliance Status</span>
            <strong className="text-amber-400 flex items-center gap-1">
              <AlertTriangle size={10} className="text-amber-400" /> REVIEW PENDING
            </strong>
          </div>
        </div>
      </div>

      {/* Footer System Status Wording Tag */}
      <div className="pt-2 text-center text-[10px] font-mono text-slate-500 uppercase tracking-widest">
        Nexus Tax & Self Assessment Readiness Hub Ready for Public Preview — accountant review required before filing, payroll submission, or tax reliance.
      </div>
    </div>
  );
}
